<template>
  <v-dialog width="700" persistent :value="show">
    <v-card>
      <v-card-title class="headline lighten-2">
        {{ t("add_new_payout_details") }}
      </v-card-title>

      <v-card-text class="pt-4">
        <ca-alert v-if="errors.purse_type" type="error">{{ errors.purse_type[0] }}</ca-alert>

        <v-row align="center">
          <v-col class="d-flex" cols="12" sm="12">
            <v-select
              v-model="purseType"
              :items="items"
              :hint="`${t('Fee')}: ${purseType.fee} ${t('Min_payout_is')}: ${purseType.min_withdraw_formatted}`"
              item-text="name"
              item-value="id"
              :label="t('payout_system')"
              persistent-hint
              return-object
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col class="d-flex" cols="12" sm="12">
            <v-text-field v-model="purseNumber" :label="t('payout_details')" :error-messages="errors.number" />
          </v-col>
        </v-row>

        <template v-if="purseType.id === 'cards'">
          <v-row>
            <v-col>
              <v-select v-model="card.month" :items="months" :label="t('month')" :error-messages="errors.month" />
            </v-col>
            <v-col>
              <v-select v-model="card.year" :items="years" :label="t('year')" :error-messages="errors.year" />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-text-field v-model="card.firstName" :label="t('first_name')" :error-messages="errors.first_name" />
            </v-col>
            <v-col>
              <v-text-field v-model="card.lastName" :label="t('last_name')" :error-messages="errors.last_name" />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-select
                v-model="card.currency"
                :items="currencies"
                :label="t('currency')"
                :error-messages="errors.currency"
              />
            </v-col>
          </v-row>
        </template>

        <template v-if="['trdebit', 'trcredit'].includes(purseType.id)">
          <v-row>
            <v-col>
              <v-text-field v-model="card.firstName" :label="t('first_name')" :error-messages="errors.first_name" />
            </v-col>
            <v-col>
              <v-text-field v-model="card.lastName" :label="t('last_name')" :error-messages="errors.last_name" />
            </v-col>
          </v-row>

          <v-row v-show="purseType.id === 'trdebit'">
            <v-col>
              <v-text-field v-model="card.iban" :label="t('iban')" :error-messages="errors.iban" />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-select
                v-model="card.bankName"
                :items="turkishBanks"
                :label="t('bank_name')"
                :error-messages="errors.bank_name"
              />
            </v-col>
          </v-row>
        </template>

        <v-row>
          <v-col>
            <span style="font-size: 0.85em;">{{ $t("common.all_fields_are_required") }}</span>

            <div v-if="showExchangeByRatesNotice">
              {{ t("exchange_by_rates") }}
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="close">
          {{ $t("common.close") }}
        </v-btn>
        <v-btn color="primary" :loading="saving" @click="save">
          {{ t("submit_payouts_details") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CaAlert from "@/views/components/Alert";

export default {
  name: "CaAddPurseDialog",
  components: { CaAlert },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      purseType: this.$store.state.payouts.pursesTypes[0],
      purseNumber: "",
      errors: {},
      card: {
        month: null,
        year: null,
        currency: null,
        firstName: null,
        lastName: null,
        iban: null,
        bankName: null
      },
      saving: false
    };
  },
  computed: {
    items() {
      return this.$store.state.payouts.activePursesTypes;
    },
    turkishBanks() {
      return this.$store.state.payouts.turkishBanks;
    },
    currencies() {
      return [...this.$store.state.payouts.cardCurrencies].sort();
    },
    months() {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    },
    years() {
      const startYear = new Date().getFullYear();

      let years = [startYear];

      for (let i = 0; i <= 10; i++) {
        years.push(startYear + i);
      }

      return years;
    },
    showExchangeByRatesNotice() {
      return ["qiwi", "yandex", "cards"].includes(this.purseType.id);
    }
  },
  watch: {
    show(val) {
      if (!val) {
        this.purseNumber = "";
        this.errors = {};
      }
    }
  },
  methods: {
    close() {
      this.$emit("onClose");
    },
    save() {
      if (this.saving) {
        return;
      }

      this.saving = true;

      this.errors = {};

      let data = {
        purse_type: this.purseType.id,
        number: this.purseNumber
      };

      if (["cards", "trdebit", "trcredit"].includes(this.purseType.id)) {
        data = Object.assign(data, this.card);

        data.first_name = data.firstName;
        delete data.firstName;

        data.last_name = data.lastName;
        delete data.lastName;

        if (["trdebit", "trcredit"].includes(this.purseType.id)) {
          data.bank_name = data.bankName;
          delete data.bankName;
        }
      }

      this.$store
        .dispatch("payouts/addPurse", data)
        .then(data => {
          this.saving = false;
          this.purseNumber = "";
          this.$emit("onAdd", data);
        })
        .catch(response => {
          this.saving = false;
          this.errors = response.data.errors;
        });
    },
    t(key, params) {
      return this.$t("pages.payouts." + key, params);
    }
  }
};
</script>
